import { useLanguage } from '@/contexts/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import logo from '@/assets/logo.svg';

const letterVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.3 + i * 0.04, duration: 0.4 },
  }),
};

const bannerCount = 2;

const HeroSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const [bannerIndex, setBannerIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setBannerIndex((prev) => (prev + 1) % bannerCount);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const scrollToContact = () => {
    document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const title = 'TOM ART ';
  const accent = 'MASK';

  return (
    <section ref={ref} id="home" className="relative min-h-screen flex items-end justify-center overflow-hidden">
      {/* Background media */}
      <motion.div className="absolute inset-0" style={{ y }}>
        <div className="absolute inset-0 overflow-hidden">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster="/hero/hero-poster.jpg"
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover"
          >
            <source src="/hero/hero-video.mp4" type="video/mp4" />
          </video>
        </div>
        {/* Tmavý filtr přes celé video */}
        <div className="absolute inset-0 bg-black/50" />
        {/* Bottom-only gradient for text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent" />
      </motion.div>

      {/* Content — pinned to bottom */}
      <div className="relative z-10 w-full px-4 pb-24 flex flex-col items-center">
        <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
          <motion.img
            src={logo}
            alt="Tom Art Mask logo"
            width={80}
            height={80}
            fetchPriority="high"
            decoding="async"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1.4 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="h-16 sm:h-20 w-auto drop-shadow-[0_0_20px_hsl(var(--primary)/0.3)]"
          />

          <h1
            className="text-4xl sm:text-6xl md:text-7xl font-bold tracking-wider text-foreground"
            style={{ fontFamily: 'Bebas Neue', textShadow: '0 2px 20px rgba(0,0,0,0.8)' }}
          >
            <span className="sr-only">
              Tom Art Mask – Custom airbrush hokejové brankářské masky na zakázku, Třebíč. Malování a repas masek pro brankáře.
            </span>
            <span aria-hidden="true">
              {title.split('').map((char, i) => (
                <motion.span
                  key={i}
                  custom={i}
                  variants={letterVariants}
                  initial="hidden"
                  animate="visible"
                  className="inline-block"
                  style={{ minWidth: char === ' ' ? '0.3em' : undefined }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </motion.span>
              ))}
              {accent.split('').map((char, i) => (
                <motion.span
                  key={`a-${i}`}
                  custom={title.length + i}
                  variants={letterVariants}
                  initial="hidden"
                  animate="visible"
                  className="inline-block text-primary"
                >
                  {char}
                </motion.span>
              ))}
            </span>
          </h1>

        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ duration: 1, delay: 0.6 }}
          className="mt-3 h-0.5 w-24 bg-gradient-to-r from-transparent via-primary to-transparent"
        />

        {/* Rotating banners */}
        <div className="mt-3 h-10 sm:h-12 flex items-center justify-center overflow-hidden">
          <motion.p
            key={bannerIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="text-lg sm:text-2xl md:text-3xl tracking-widest text-foreground uppercase"
            style={{ fontFamily: 'Bebas Neue', textShadow: '0 2px 16px rgba(0,0,0,0.7)' }}
          >
            {t.hero.banners[bannerIndex]}
          </motion.p>
        </div>

        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.2 }}
          onClick={scrollToContact}
          className="btn-shimmer mt-4 px-8 py-3 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm hover:bg-primary/90 transition-colors rounded-sm"
          style={{ boxShadow: '0 0 24px hsl(195 95% 50% / 0.4)' }}
        >
          {t.hero.cta}
        </motion.button>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="w-5 h-8 border-2 border-muted-foreground rounded-full flex justify-center">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-1 h-1 bg-primary rounded-full mt-1.5"
          />
        </div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
